<template>
  <div id="scroll-view">
    <v-card tile>
      <edit-asset-button
        :link="`/articles/edit/${data.slug}`"
      ></edit-asset-button>
      <v-card-title class="display-2 d-flex justify-end break-word">
        <back-button></back-button>
      </v-card-title>
      <v-card-title class="display-2 d-flex justify-space-between break-word">
        {{ data.title }}
      </v-card-title>
      <v-card-text class="text-capitalize d-flex justify-between">
        <v-flex class="text-left">
          {{ displayDate }}
          <span class="text-lowercase">în categoria</span>
          <span class="font-weight-bold">
            {{ data.category_name }}
          </span>
        </v-flex>
      </v-card-text>
      <template v-if="data.cover_image_location === 'top'">
        <v-img
            :src="data.cover_image"
          :alt="data.title"
          aspect-ratio="1.5"
          width="100%"
        ></v-img>
      </template>

      <v-card-text>
        <TextWidget :key="data.id" :text="data.description"></TextWidget>

        <map-renderer :show-map="showMap" :data="data.map"></map-renderer>

        <ProjectView :project="data.project"></ProjectView>

        <poll-widget :poll-data="data.poll"></poll-widget>

        <gallery-widget :data="data" />

        <DocumentsList :documents="data.documents"></DocumentsList>
      </v-card-text>
      <template v-if="data.cover_image_location === 'bottom'">
        <v-img
          :src="data.cover_image"
          :alt="data.title"
          aspect-ratio="1.5"
          width="100%"
        ></v-img>
      </template>
      <v-card-text>
        <client-only>
          <SocialShare :article="data"></SocialShare>
        </client-only>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ProjectView from '~/components/article/cards/ProjectView'
import DocumentsList from '~/components/article/DocumentsList'
import MapRenderer from '~/components/article/MapRenderer'
import BackButton from '~/components/common/BackButton'
import EditAssetButton from '~/components/common/EditAssetButton'
import SocialShare from '~/components/common/SocialShare'
import GalleryWidget from '~/components/widget/GalleryWidget'
import PollWidget from '~/components/widget/PollWidget'
import TextWidget from '~/components/widget/TextWidget'

export default {
  name: 'ArticleView',
  components: {
    DocumentsList,
    GalleryWidget,
    PollWidget,
    ProjectView,
    TextWidget,
    EditAssetButton,
    BackButton,
    SocialShare,
    MapRenderer,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      projectTab: null,
    }
  },
  head() {
    return {
      title: this.data.title,
      link: [
        {
          rel: 'canonical',
          href: this.href,
        },
      ],
      meta: [
        // Robots
        {
          property: 'robots',
          content: 'index, follow',
        },
        {
          property: 'googlebot',
          content:
            'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        },
        {
          property: 'bingbot',
          content:
            'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        },
        // General
        {
          property: 'description',
          content: this.data.short_description,
        },
        // Google
        {
          itemprop: 'name',
          content: this.data.title || '',
        },
        {
          itemprop: 'headline',
          content: this.data.title || '',
        },
        {
          itemprop: 'description',
          content: this.data.short_description,
        },
        {
          itemprop: 'image',
          content: this.data.cover_image,
        },
        {
          itemprop: 'datePublished',
          content: this.data.created_at,
        },
        {
          itemprop: 'dateModified',
          content: this.data.updated_at,
        },
        {
          itemprop: 'author',
          content: 'Hirama Tech',
        },
        // Twitter
        {
          property: 'twitter:site',
          content: '@website-username',
        },
        {
          property: 'twitter:title',
          content: this.data.title || '',
        },
        {
          property: 'twitter:description',
          content: this.data.short_description,
        },
        {
          property: 'twitter:card',
          content: `summary_large_image`,
        },
        {
          property: 'twitter:image',
          content: this.data.cover_image,
        },
        {
          property: 'twitter:image:alt',
          content: 'Imagine articol',
        },
        // Facebook
        {
          property: 'og:title',
          content: this.data.title || '',
        },
        {
          property: 'og:description',
          content: this.data.short_description,
        },
        {
          property: 'og:url',
          content: this.href,
        },
        {
          property: 'og:image',
          content: this.data.cover_image,
        },
        {
          property: 'og:image:secure_url',
          content: this.data.cover_image,
        },
        {
          property: 'og:image:alt',
          content: this.data.title || '',
        },
        {
          property: 'og:image::width',
          content: 400,
        },
        {
          property: 'og:image::height',
          content: 300,
        },
        {
          property: 'og:type',
          content: `article`,
        },
        {
          property: 'og:updated_time',
          content: this.data.updated_at,
        },
        {
          property: 'og:site_name',
          content: `Platformă primării`,
        },
        {
          property: 'fb:app_id',
          content: `3000067026939796`,
        },
        {
          property: 'og:locale',
          content: 'ro_RO',
        },
        // Essential
        {
          property: 'article:publisher',
          content: this.data.facebook_url,
        },
        {
          property: 'article:published_time',
          content: this.data.created_at,
        },
        {
          property: 'article:modified_time',
          content: this.data.updated_at,
        },
        {
          property: 'article:section',
          content: this.data.category_name,
        },
        {
          property: 'article:section',
          content: this.data.category_name,
        },
      ],
    }
  },
  computed: {
    href() {
      return 'https://' + this.$store.state.app.tenant + this.$route.fullPath
    },
    type() {
      if (this.data.article_type) {
        return this.data.article_type.blocks
      }

      return []
    },
    displayDate() {
      if (this.type.includes('display_date')) {
        return this.$options.filters.formatDate(this.data.display_date, 'll')
      }

      if (this.type.includes('display_date_time')) {
        return this.$options.filters.formatDate(this.data.display_date, 'lll')
      }

      return ''
    },
    showMap() {
      return this.type.includes('map')
    },
  },
  async mounted() {
    await this.$nextTick()
    if (document.getElementById('scroll-view')) {
      await this.$vuetify.goTo('#scroll-view', {
        offset: 100,
      })
    }
  },
}
</script>
